import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth, useUser } from '../../contexts/AuthContext';
import { UserRole } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requireEmailVerification?: boolean;
  requireApproval?: boolean;
}

/**
 * Protected Route Component
 * Handles authentication and authorization for protected pages
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireEmailVerification = false,
  requireApproval = true,
}) => {
  const { authState } = useAuth();
  const user = useUser();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (authState.loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="textSecondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!authState.isAuthenticated || !user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Check email verification requirement
  if (requireEmailVerification && !user.isEmailVerified) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, message: 'Email verification required' }}
        replace
      />
    );
  }

  // Check approval requirement
  if (requireApproval && !user.isApproved) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, message: 'Account approval pending' }}
        replace
      />
    );
  }

  // Note: Profile completion check removed to prevent infinite redirect loop
  // Dashboard will show profile completion prompts via UI instead

  // Check role requirement
  if (requiredRole) {
    const roleHierarchy: Record<UserRole, number> = {
      viewer: 1,
      contributor: 2,
      editor: 3,
      admin: 4,
    };

    const userRoleLevel = roleHierarchy[user.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    if (userRoleLevel < requiredRoleLevel) {
      return (
        <Navigate
          to="/dashboard"
          state={{
            from: location.pathname,
            message: `Access denied. Required role: ${requiredRole}`
          }}
          replace
        />
      );
    }
  }

  // All checks passed, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;